// resultimage.js — render a query-result table to a PNG blob suitable for the
// clipboard. The image is a standalone screenshot: a header row above the table
// with the named-statement label (or a truncated SQL preview), the database the
// run targeted, and the capture timestamp. Columns auto-fit to their content
// via canvas.measureText, with no width cap — the caller said "I don't care
// how wide it gets." Used by ExportMenu's "📷 Copy as image" item.
//
// No external dependency. The output is a fixed light theme (white background,
// dark ink, subtle row borders) so the image pastes cleanly into any document,
// Slack, email, or issue tracker regardless of the user's Ninocat theme.

const FONT_FAMILY = "'Fira Mono', 'Cascadia Mono', Consolas, monospace";
const FONT_SIZE_BODY    = 13;
const FONT_SIZE_HEADER  = 14;
const FONT_SIZE_TITLE   = 18;
const FONT_SIZE_META    = 12;
const ROW_HEIGHT        = 24;
const HEAD_ROW_HEIGHT   = 28;
const CELL_PAD_X        = 12;
const TITLE_PAD         = 18;
const META_PAD          = 10;
const MAX_ROWS          = 500;     // cap so a 100k-row result doesn't OOM the renderer
const SQL_PREVIEW_MAX   = 110;     // characters before ellipsis in the SQL preview line

// Theme — light, neutral, document-friendly.
const C_BG          = '#ffffff';
const C_FG          = '#222831';
const C_FG_MUTED    = '#5a6473';
const C_HEAD_BG     = '#f1f3f7';
const C_HEAD_FG     = '#1a2030';
const C_ROW_ALT_BG  = '#fafbfd';
const C_BORDER      = '#dde1e8';
const C_ACCENT      = '#2563eb';

// pickTitle — what to show on the first header line. If the user named the
// statement (~name~ or -- @name:), use the name; otherwise fall back to a
// trimmed/ellipsized SQL preview, which is more useful than "Result #5".
function pickTitle(label, sql) {
  const isDefault = !label || /^Result #\d+$/.test(label);
  if (!isDefault) return label;
  return sqlPreview(sql || '');
}

function sqlPreview(sql) {
  const oneLine = sql.replace(/--[^\n]*\n?|\/\*[\s\S]*?\*\//g, ' ').replace(/\s+/g, ' ').trim();
  if (oneLine.length <= SQL_PREVIEW_MAX) return oneLine;
  return oneLine.slice(0, SQL_PREVIEW_MAX - 1).trimEnd() + '…';
}

function formatTimestamp(ms) {
  const d = ms ? new Date(ms) : new Date();
  // Local time, ISO-ish but human (YYYY-MM-DD HH:MM:SS).
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// Stringify a cell value the same way the on-screen grid does (NULL for nullish,
// otherwise String(v)). Mirrors lib/format.cellAsText but inlined to avoid an
// extra import for one tiny helper.
function cellText(v) {
  if (v === null || v === undefined) return 'NULL';
  return String(v);
}

// Measure column widths by running canvas.measureText against the header AND
// every visible cell value, picking the max + padding. No artificial cap.
function measureColumnWidths(ctx, columns, displayOrder, rows, capRows) {
  const cols = displayOrder.map((name) => {
    const idx = columns.findIndex((c) => c.name === name);
    return { name, idx };
  });
  // Header pass — bold font for accurate metrics.
  ctx.font = `600 ${FONT_SIZE_HEADER}px ${FONT_FAMILY}`;
  const widths = cols.map((c) => ctx.measureText(c.name).width);
  // Body pass — regular weight, sampled across the (capped) row count.
  ctx.font = `${FONT_SIZE_BODY}px ${FONT_FAMILY}`;
  const sample = Math.min(rows.length, capRows);
  for (let r = 0; r < sample; r++) {
    const row = rows[r];
    for (let c = 0; c < cols.length; c++) {
      const idx = cols[c].idx;
      if (idx < 0) continue;
      const w = ctx.measureText(cellText(row[idx])).width;
      if (w > widths[c]) widths[c] = w;
    }
  }
  return widths.map((w) => Math.ceil(w + CELL_PAD_X * 2));
}

// renderResultImage — returns a Promise<Blob> (PNG). meta carries the header
// content; gridRows / columns / displayOrder are the data to draw. Caller
// supplies `displayOrder` to honour the on-screen column order (hidden /
// reordered columns aren't drawn).
export async function renderResultImage({ label, sql, database, createdAt, columns, rows, displayOrder, server }) {
  // Defensive defaults so an explain or zero-row tab still produces SOMETHING.
  columns     = Array.isArray(columns)     ? columns     : [];
  rows        = Array.isArray(rows)        ? rows        : [];
  displayOrder = Array.isArray(displayOrder) && displayOrder.length
    ? displayOrder.filter((n) => columns.some((c) => c.name === n))
    : columns.map((c) => c.name);
  if (displayOrder.length === 0) throw new Error('No columns to render.');

  // Use a tiny offscreen canvas for measurement so we don't pay for the real
  // canvas's size until we know it.
  const probe = document.createElement('canvas');
  probe.width = probe.height = 16;
  const pctx = probe.getContext('2d');
  if (!pctx) throw new Error('Canvas 2D context unavailable.');
  const rowCount = Math.min(rows.length, MAX_ROWS);
  const colWidths = measureColumnWidths(pctx, columns, displayOrder, rows, rowCount);
  const tableWidth = colWidths.reduce((a, w) => a + w, 0);

  // Header section size: title line + meta line + paddings.
  // We measure with the same probe so wrapping isn't an issue (we don't wrap —
  // the title is intentionally one line, ellipsized if needed).
  const headerHeight = TITLE_PAD + FONT_SIZE_TITLE + 4 + FONT_SIZE_META + TITLE_PAD;
  const tableHeight  = HEAD_ROW_HEIGHT + rowCount * ROW_HEIGHT;
  const truncated    = rows.length > rowCount;
  const footerHeight = truncated ? ROW_HEIGHT : 0;
  const width  = Math.max(tableWidth, 480);
  const height = headerHeight + tableHeight + footerHeight;

  // Render on a device-pixel-doubled canvas so text isn't blurry on retina.
  // The CSS-size dimensions stay `width × height` but the bitmap is DPR×.
  const dpr = window.devicePixelRatio || 1;
  const canvas = document.createElement('canvas');
  canvas.width  = Math.round(width  * dpr);
  canvas.height = Math.round(height * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable.');
  ctx.scale(dpr, dpr);
  ctx.textBaseline = 'middle';

  // Background.
  ctx.fillStyle = C_BG;
  ctx.fillRect(0, 0, width, height);

  // ── Header section ──
  const title = pickTitle(label, sql);
  ctx.fillStyle = C_FG;
  ctx.font = `700 ${FONT_SIZE_TITLE}px ${FONT_FAMILY}`;
  // Ellipsize title to fit if (somehow) wider than the canvas.
  const titleY = TITLE_PAD + FONT_SIZE_TITLE / 2;
  drawClippedText(ctx, title, CELL_PAD_X, titleY, width - CELL_PAD_X * 2);
  // Meta line — database · server flavour+version · timestamp · "Ninocat".
  const meta = [
    database ? `📂 ${database}` : null,
    server ? `🖧 ${server}` : null,
    `🕒 ${formatTimestamp(createdAt)}`,
    `${rows.length} row${rows.length === 1 ? '' : 's'}`,
  ].filter(Boolean).join('   ');
  ctx.fillStyle = C_FG_MUTED;
  ctx.font = `${FONT_SIZE_META}px ${FONT_FAMILY}`;
  const metaY = TITLE_PAD + FONT_SIZE_TITLE + 4 + FONT_SIZE_META / 2;
  drawClippedText(ctx, meta, CELL_PAD_X, metaY, width - CELL_PAD_X * 2);

  // Divider between header and table.
  ctx.fillStyle = C_BORDER;
  ctx.fillRect(0, headerHeight - 1, width, 1);

  // ── Table header row ──
  let y = headerHeight;
  ctx.fillStyle = C_HEAD_BG;
  ctx.fillRect(0, y, tableWidth, HEAD_ROW_HEIGHT);
  ctx.fillStyle = C_HEAD_FG;
  ctx.font = `600 ${FONT_SIZE_HEADER}px ${FONT_FAMILY}`;
  let x = 0;
  for (let c = 0; c < displayOrder.length; c++) {
    drawClippedText(ctx, displayOrder[c], x + CELL_PAD_X, y + HEAD_ROW_HEIGHT / 2, colWidths[c] - CELL_PAD_X * 2);
    x += colWidths[c];
  }
  // Bottom border of header row.
  ctx.fillStyle = C_BORDER;
  ctx.fillRect(0, y + HEAD_ROW_HEIGHT - 1, tableWidth, 1);
  y += HEAD_ROW_HEIGHT;

  // ── Data rows ──
  ctx.font = `${FONT_SIZE_BODY}px ${FONT_FAMILY}`;
  for (let r = 0; r < rowCount; r++) {
    if (r % 2 === 1) {
      ctx.fillStyle = C_ROW_ALT_BG;
      ctx.fillRect(0, y, tableWidth, ROW_HEIGHT);
    }
    x = 0;
    const row = rows[r];
    for (let c = 0; c < displayOrder.length; c++) {
      const idx = columns.findIndex((cc) => cc.name === displayOrder[c]);
      const v = idx >= 0 ? row[idx] : null;
      const isNull = v === null || v === undefined;
      ctx.fillStyle = isNull ? C_FG_MUTED : C_FG;
      drawClippedText(ctx, cellText(v), x + CELL_PAD_X, y + ROW_HEIGHT / 2, colWidths[c] - CELL_PAD_X * 2);
      x += colWidths[c];
    }
    // Subtle row separator.
    ctx.fillStyle = C_BORDER;
    ctx.fillRect(0, y + ROW_HEIGHT - 1, tableWidth, 1);
    y += ROW_HEIGHT;
  }

  // Vertical column separators across the entire table area.
  ctx.fillStyle = C_BORDER;
  let cx = 0;
  for (let c = 0; c < colWidths.length - 1; c++) {
    cx += colWidths[c];
    ctx.fillRect(cx - 0.5, headerHeight, 1, tableHeight);
  }

  // Right-edge border so the table reads as a contained block.
  ctx.fillRect(tableWidth - 1, headerHeight, 1, tableHeight);

  // ── Truncation footer ──
  if (truncated) {
    ctx.fillStyle = C_BG;
    ctx.fillRect(0, y, width, ROW_HEIGHT);
    ctx.fillStyle = C_ACCENT;
    ctx.font = `italic ${FONT_SIZE_BODY}px ${FONT_FAMILY}`;
    const more = rows.length - rowCount;
    drawClippedText(ctx, `… ${more.toLocaleString()} more row${more === 1 ? '' : 's'} truncated from the image (full result still in the tab)`,
                    CELL_PAD_X, y + ROW_HEIGHT / 2, width - CELL_PAD_X * 2);
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error('Canvas toBlob returned null.')); return; }
      resolve(blob);
    }, 'image/png');
  });
}

// drawClippedText — draws `text` with the given fillStyle, clipping to maxW.
// Uses a binary search to find the longest prefix that fits, then appends an
// ellipsis. Avoids the per-frame layout cost of a real CSS truncate.
function drawClippedText(ctx, text, x, y, maxW) {
  if (!text) return;
  if (ctx.measureText(text).width <= maxW) { ctx.fillText(text, x, y); return; }
  // Binary search the longest prefix that, with "…", still fits.
  const ell = '…';
  let lo = 0, hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + ell).width <= maxW) lo = mid; else hi = mid - 1;
  }
  ctx.fillText(text.slice(0, lo) + ell, x, y);
}

// copyResultImage — high-level helper: render → toBlob → ClipboardItem. The
// caller wraps it for toast + error handling. Throws when the clipboard API
// isn't available (older browsers / non-HTTPS contexts), so the caller can
// fall back to "Download image" if it wants.
export async function copyResultImage(opts) {
  const blob = await renderResultImage(opts);
  if (!navigator.clipboard || !window.ClipboardItem) {
    throw new Error('Clipboard image API unavailable — requires a modern browser on HTTPS or localhost.');
  }
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
  return blob.size;
}
